import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../utils/cn";
import { Button, Input, Textarea, Select } from "../ui";
import { ArrowRight, Loader2, CheckCircle, AlertCircle } from "lucide-react";

interface FormData {
  // Step 1: About you
  name: string;
  email: string;
  jobTitle: string;
  // Step 2: About company
  company: string;
  website: string;
  industry: string;
  companySize: string;
  targetMarket: string;
  // Step 3: Current sales process
  monthlyOutboundVolume: string;
  salesTeamSize: string;
  currentChallenge: string;
  // Step 4: Goals
  goals: string;
  startTimeline: string;
  budgetRange: string;
  heardAboutUs: string;
}

const initialFormData: FormData = {
  name: "",
  email: "",
  jobTitle: "",
  company: "",
  website: "",
  industry: "",
  companySize: "",
  targetMarket: "",
  monthlyOutboundVolume: "",
  salesTeamSize: "",
  currentChallenge: "",
  goals: "",
  startTimeline: "",
  budgetRange: "",
  heardAboutUs: "",
};

const steps = [
  { id: 1, title: "About You", description: "Basic contact information" },
  {
    id: 2,
    title: "About Your Company",
    description: "Company details and target market",
  },
  {
    id: 3,
    title: "Current Sales Process",
    description: "Your outbound and sales setup",
  },
  { id: 4, title: "Goals & Timeline", description: "What you want to achieve" },
];

const industries = [
  { value: "", label: "Select industry" },
  { value: "saas", label: "B2B SaaS" },
  { value: "ai", label: "AI / Machine Learning" },
  { value: "software", label: "Software / Technology" },
  { value: "it-services", label: "IT Services / Consulting" },
  { value: "fintech", label: "FinTech" },
  { value: "healthtech", label: "HealthTech" },
  { value: "edtech", label: "EdTech" },
  { value: "martech", label: "MarTech / SalesTech" },
  { value: "cybersecurity", label: "Cybersecurity" },
  { value: "devtools", label: "Developer Tools" },
  { value: "other", label: "Other" },
];

const companySizes = [
  { value: "", label: "Company size" },
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "501-1000", label: "501-1000 employees" },
  { value: "1000+", label: "1000+ employees" },
];

const outboundVolumes = [
  { value: "", label: "Monthly outbound volume" },
  { value: "none", label: "Not doing outbound yet" },
  { value: "under-500", label: "Under 500 contacts/month" },
  { value: "500-2000", label: "500-2,000 contacts/month" },
  { value: "2000-5000", label: "2,000-5,000 contacts/month" },
  { value: "5000-10000", label: "5,000-10,000 contacts/month" },
  { value: "10000+", label: "10,000+ contacts/month" },
];

const teamSizes = [
  { value: "", label: "Sales team size" },
  { value: "founder-only", label: "Founder-led sales only" },
  { value: "1-2", label: "1-2 sales reps" },
  { value: "3-5", label: "3-5 sales reps" },
  { value: "6-10", label: "6-10 sales reps" },
  { value: "11-20", label: "11-20 sales reps" },
  { value: "20+", label: "20+ sales reps" },
];

const timelines = [
  { value: "", label: "When to start" },
  { value: "asap", label: "ASAP" },
  { value: "30-days", label: "Within 30 days" },
  { value: "1-3-months", label: "1-3 months" },
  { value: "exploring", label: "Just exploring" },
];

const budgets = [
  { value: "", label: "Budget range (optional)" },
  { value: "under-5k", label: "Under $5K/month" },
  { value: "5k-10k", label: "$5K-$10K/month" },
  { value: "10k-25k", label: "$10K-$25K/month" },
  { value: "25k-50k", label: "$25K-$50K/month" },
  { value: "50k+", label: "$50K+/month" },
];

const sources = [
  { value: "", label: "How did you hear about us?" },
  { value: "google", label: "Google Search" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "referral", label: "Referral" },
  { value: "direct", label: "Direct / Word of mouth" },
  { value: "content", label: "Blog / Content" },
  { value: "other", label: "Other" },
];

export function QualificationForm({
  onSubmit,
  calComUrl,
}: {
  onSubmit?: (data: FormData) => void | Promise<void>;
  calComUrl?: string;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const formElement = useRef<HTMLFormElement>(null);
  const previousStep = useRef(currentStep);
  useEffect(() => {
    if (previousStep.current === currentStep) return;
    previousStep.current = currentStep;
    const input = formElement.current?.querySelector<HTMLInputElement>(
      "fieldset input, fieldset select, fieldset textarea",
    );
    input?.focus({ preventScroll: true });
    input?.scrollIntoView({
      block: "center",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  }, [currentStep]);

  const optionLabel = (
    options: { value: string; label: string }[],
    value: string,
  ) =>
    options.find((option) => option.value === value)?.label ||
    value ||
    "Not specified";
  const summary = [
    `Name: ${formData.name}`,
    `Work email: ${formData.email}`,
    `Role: ${formData.jobTitle}`,
    `Company: ${formData.company}`,
    `Website: ${formData.website}`,
    `Industry: ${optionLabel(industries, formData.industry)}`,
    `Company size: ${optionLabel(companySizes, formData.companySize)}`,
    `Target market: ${formData.targetMarket}`,
    `Outbound volume: ${optionLabel(outboundVolumes, formData.monthlyOutboundVolume)}`,
    `Sales team: ${optionLabel(teamSizes, formData.salesTeamSize)}`,
    `Current challenge: ${formData.currentChallenge}`,
    `Goals: ${formData.goals}`,
    `Timeline: ${optionLabel(timelines, formData.startTimeline)}`,
    `Budget: ${optionLabel(budgets, formData.budgetRange)}`,
    `Source: ${optionLabel(sources, formData.heardAboutUs)}`,
  ].join("\n");
  const schedulingUrl = calComUrl ? new URL(calComUrl) : null;
  if (schedulingUrl) {
    schedulingUrl.searchParams.set("name", formData.name);
    schedulingUrl.searchParams.set("email", formData.email);
    schedulingUrl.searchParams.set("notes", summary);
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (!formData.email.trim()) newErrors.email = "Email is required";
      else if (!formData.email.includes("@"))
        newErrors.email = "Valid email is required";
      if (!formData.jobTitle.trim())
        newErrors.jobTitle = "Job title is required";
    }

    if (step === 2) {
      if (!formData.company.trim())
        newErrors.company = "Company name is required";
      if (!formData.website.trim()) newErrors.website = "Website is required";
      else if (!formData.website.includes("."))
        newErrors.website = "Valid website is required";
      if (!formData.industry) newErrors.industry = "Industry is required";
      if (!formData.companySize)
        newErrors.companySize = "Company size is required";
      if (!formData.targetMarket.trim())
        newErrors.targetMarket = "Target market is required";
    }

    if (step === 3) {
      if (!formData.monthlyOutboundVolume)
        newErrors.monthlyOutboundVolume = "Please select volume";
      if (!formData.salesTeamSize)
        newErrors.salesTeamSize = "Please select team size";
      if (!formData.currentChallenge.trim())
        newErrors.currentChallenge = "Biggest challenge is required";
    }

    if (step === 4) {
      if (!formData.goals.trim()) newErrors.goals = "Goals are required";
      if (!formData.startTimeline)
        newErrors.startTimeline = "Please select timeline";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < 4) {
      nextStep();
      return;
    }
    if (!validateStep(4)) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      if (onSubmit) await onSubmit(formData);
      setSubmitStatus("success");
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  if (submitStatus === "success") {
    return (
      <div className="text-center py-16 animate-scale-in" role="status">
        <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-success" aria-hidden="true" />
        </div>
        <h2 className="h2 text-text-primary mb-3">Ready for the next step</h2>
        <p className="body text-text-secondary mb-8 max-w-md mx-auto">
          {onSubmit
            ? "Your details have been submitted. Continue to the calendar to schedule your strategy call."
            : "Your call details are ready. Review the summary below, then confirm your details in the calendar to complete your booking."}
        </p>
        <label htmlFor="call-summary" className="label-base text-left">
          Your call preparation summary
        </label>
        <textarea
          id="call-summary"
          className="input-base mb-6 text-left"
          value={summary}
          readOnly
          rows={8}
          onFocus={(event) => event.currentTarget.select()}
        />
        {calComUrl && (
          <a
            href={schedulingUrl?.toString()}
            className="btn btn-primary btn-lg"
          >
            Continue to Scheduling
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        )}
      </div>
    );
  }

  return (
    <form
      ref={formElement}
      onSubmit={handleSubmit}
      onKeyDown={(event) => {
        if (
          currentStep < 4 &&
          event.key === "Enter" &&
          event.target instanceof HTMLInputElement
        ) {
          event.preventDefault();
          nextStep();
        }
      }}
      className="w-full max-w-2xl mx-auto"
      noValidate
    >
      {/* Progress Indicator */}
      <div
        className="mb-8"
        role="progressbar"
        aria-valuenow={currentStep}
        aria-valuemin={1}
        aria-valuemax={4}
        aria-label="Form progress"
      >
        <div className="grid grid-cols-4 gap-1 relative">
          <div
            className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-1 bg-border"
            aria-hidden="true"
          >
            <div
              className="h-full bg-accent transition-all duration-500"
              style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
            />
          </div>
          {steps.map((step, index) => (
            <div
              key={step.id}
              className="relative z-10 flex flex-col items-center min-w-0"
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-full border-2 flex items-center justify-center font-medium text-sm transition-all duration-300",
                  index + 1 < currentStep
                    ? "bg-accent border-accent text-accent-foreground"
                    : index + 1 === currentStep
                      ? "border-accent text-accent bg-background"
                      : "border-border text-text-muted bg-background",
                )}
              >
                {index + 1 < currentStep ? <CheckCircle size={18} /> : step.id}
              </div>
              <span
                className={cn(
                  "caption mt-2 text-center max-w-[80px]",
                  index + 1 <= currentStep
                    ? "text-text-primary"
                    : "text-text-muted",
                )}
              >
                {step.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Form Steps */}
      <div
        className="animate-fade-in"
        role="group"
        aria-labelledby={`step-${currentStep}-title`}
      >
        {/* Step 1: About You */}
        {currentStep === 1 && (
          <fieldset>
            <legend id="step-1-title" className="visually-hidden">
              Step 1: About You
            </legend>
            <div className="space-y-5">
              <Input
                label="Full Name"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                error={errors.name}
                autoComplete="name"
                required
              />
              <Input
                label="Work Email"
                placeholder="john@company.com"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                error={errors.email}
                autoComplete="email"
                required
              />
              <Input
                label="Job Title"
                placeholder="Head of Sales, Founder, VP Growth..."
                value={formData.jobTitle}
                onChange={(e) => handleChange("jobTitle", e.target.value)}
                error={errors.jobTitle}
                autoComplete="organization-title"
                required
              />
            </div>
          </fieldset>
        )}

        {/* Step 2: About Company */}
        {currentStep === 2 && (
          <fieldset>
            <legend id="step-2-title" className="visually-hidden">
              Step 2: About Your Company
            </legend>
            <div className="space-y-5">
              <Input
                label="Company Name"
                placeholder="Acme Inc."
                value={formData.company}
                onChange={(e) => handleChange("company", e.target.value)}
                error={errors.company}
                autoComplete="organization"
                required
              />
              <Input
                label="Company Website"
                placeholder="https://acme.com"
                type="url"
                value={formData.website}
                onChange={(e) => handleChange("website", e.target.value)}
                error={errors.website}
                autoComplete="url"
                required
              />
              <Select
                label="Industry"
                placeholder="Select industry"
                options={industries}
                value={formData.industry}
                onChange={(e) => handleChange("industry", e.target.value)}
                error={errors.industry}
                required
              />
              <Select
                label="Company Size"
                placeholder="Company size"
                options={companySizes}
                value={formData.companySize}
                onChange={(e) => handleChange("companySize", e.target.value)}
                error={errors.companySize}
                required
              />
              <Input
                label="Target Market / Geography"
                placeholder="e.g., US B2B SaaS, EMEA Fintech, North America Mid-market..."
                value={formData.targetMarket}
                onChange={(e) => handleChange("targetMarket", e.target.value)}
                error={errors.targetMarket}
                hint="Describe your ideal customer profile and target geography"
                required
              />
            </div>
          </fieldset>
        )}

        {/* Step 3: Current Sales Process */}
        {currentStep === 3 && (
          <fieldset>
            <legend id="step-3-title" className="visually-hidden">
              Step 3: Current Sales Process
            </legend>
            <div className="space-y-5">
              <Select
                label="Current Monthly Outbound Volume"
                placeholder="Monthly outbound volume"
                options={outboundVolumes}
                value={formData.monthlyOutboundVolume}
                onChange={(e) =>
                  handleChange("monthlyOutboundVolume", e.target.value)
                }
                error={errors.monthlyOutboundVolume}
                required
              />
              <Select
                label="Current Sales Team Size"
                placeholder="Sales team size"
                options={teamSizes}
                value={formData.salesTeamSize}
                onChange={(e) => handleChange("salesTeamSize", e.target.value)}
                error={errors.salesTeamSize}
                required
              />
              <Textarea
                label="Biggest Sales / Outbound Challenge"
                placeholder="e.g., Low reply rates, no time for prospecting, bad data quality, no repeatable process..."
                value={formData.currentChallenge}
                onChange={(e) =>
                  handleChange("currentChallenge", e.target.value)
                }
                error={errors.currentChallenge}
                rows={4}
                required
              />
            </div>
          </fieldset>
        )}

        {/* Step 4: Goals */}
        {currentStep === 4 && (
          <fieldset>
            <legend id="step-4-title" className="visually-hidden">
              Step 4: Goals & Timeline
            </legend>
            <div className="space-y-5">
              <Textarea
                label="What Do You Want to Achieve?"
                placeholder="e.g., Build predictable pipeline, scale outbound to $1M ARR, reduce SDR manual work, enter new market..."
                value={formData.goals}
                onChange={(e) => handleChange("goals", e.target.value)}
                error={errors.goals}
                rows={4}
                required
              />
              <Select
                label="When Are You Looking to Start?"
                placeholder="When to start"
                options={timelines}
                value={formData.startTimeline}
                onChange={(e) => handleChange("startTimeline", e.target.value)}
                error={errors.startTimeline}
                required
              />
              <Select
                label="Budget Range (Optional)"
                placeholder="Budget range (optional)"
                options={budgets}
                value={formData.budgetRange}
                onChange={(e) => handleChange("budgetRange", e.target.value)}
              />
              <Select
                label="How Did You Hear About Us?"
                placeholder="How did you hear about us?"
                options={sources}
                value={formData.heardAboutUs}
                onChange={(e) => handleChange("heardAboutUs", e.target.value)}
              />
            </div>
          </fieldset>
        )}
      </div>

      {/* Navigation */}
      <div className="form-navigation mt-8 pt-6 border-t border-border">
        <Button
          type="button"
          variant="secondary"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          Back
        </Button>
        {currentStep < 4 ? (
          <Button
            type="button"
            variant="primary"
            onClick={nextStep}
            rightIcon={<ArrowRight size={18} />}
          >
            Continue
          </Button>
        ) : (
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            rightIcon={
              isSubmitting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <ArrowRight size={18} />
              )
            }
          >
            {isSubmitting ? "Submitting..." : "Schedule Strategy Call"}
          </Button>
        )}
      </div>

      {submitStatus === "error" && (
        <div
          className="mt-4 p-4 bg-error/10 border border-error/30 rounded-lg flex items-center gap-3 text-error"
          role="alert"
        >
          <AlertCircle size={20} aria-hidden="true" />
          <p>
            Something went wrong. Please try again or email us directly at
            hello@uniqreach.com
          </p>
        </div>
      )}

      <p className="caption text-text-muted text-center mt-6">
        By submitting, you agree to our{" "}
        <a href="/privacy" className="link-underline">
          Privacy Policy
        </a>{" "}
        and consent to being contacted about your inquiry.
      </p>
    </form>
  );
}

export function BookingCTA() {
  return (
    <section
      className="section booking-cta"
      aria-labelledby="booking-cta-title"
    >
      <div className="container">
        <div className="booking-cta-inner">
          <div>
            <span className="label text-accent">
              Let's build what comes next
            </span>
            <h2 id="booking-cta-title">
              Give your sales team
              <br />a better starting point.
            </h2>
            <p>
              Tell us where you want to grow. Let's explore the system that
              could help you get there.
            </p>
          </div>
          <div className="booking-cta-action">
            <Link to="/book" className="btn btn-primary btn-lg">
              Book a Strategy Call
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <span>A focused conversation. Clear next steps.</span>
          </div>
        </div>
      </div>
    </section>
  );
}
